import {
  Component,
  EventEmitter,
  OnInit,
  Output,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
} from '@angular/core';
import { CredentialRequest } from '../../../types/credencial-request.type';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { CredentialsService } from '../../../services/credentials.service';
import { CredentialsViewComponent } from '../credentials-view/credentials-view.component';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { Router } from '@angular/router';
import { PeopleService } from '../../../services/people.service';
import { CredentialsStateService } from '../../../services/credentials-state.service';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { DropdownModule } from 'primeng/dropdown';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { CardModule } from 'primeng/card';
import { ToastModule } from 'primeng/toast';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { InputSwitchModule } from 'primeng/inputswitch';

@Component({
  selector: 'app-credentials-inform',
  standalone: true,
  providers: [MessageService],
  imports: [
    ReactiveFormsModule,
    CommonModule,
    ButtonModule,
    InputTextModule,
    InputTextareaModule,
    DropdownModule,
    ToggleButtonModule,
    CardModule,
    ToastModule,
    ProgressSpinnerModule,
    InputSwitchModule,
  ],
  template: `
    <p-toast></p-toast>
    <div class="credentials-dialog">
      <h2 class="dialog-title">Dados da Credencial</h2>
      
      <form [formGroup]="form" class="credential-form">
        <!-- Beneficiário -->
        <div class="form-field">
          <label class="field-label">Beneficiário</label>
          <input
            pInputText
            formControlName="recipientName"
            class="p-inputtext-lg text-capitalize"
            [disabled]="true"
          />
        </div>

        <!-- Solicitante -->
        <div class="form-field">
          <label class="field-label">Solicitante</label>
          <input
            pInputText
            formControlName="applicantName"
            class="p-inputtext-lg text-capitalize"
            [disabled]="true"
          />
        </div>

        <!-- Número do Processo -->
        <div class="form-field">
          <label class="field-label">Número do Processo <span class="required">*</span></label>
          <input
            #processNumberInput
            pInputText
            formControlName="processNumber"
            placeholder="Ex: P000123/2025"
            maxlength="12"
            (input)="formatProcessNumber($event)"
            (blur)="validateProcessNumber()"
            (keypress)="onKeyPress($event)"
            class="p-inputtext-lg text-uppercase"
            [class.p-invalid]="form.get('processNumber')?.errors?.['invalidFormat']"
            autocomplete="off"
          />
          @if(form.get('processNumber')?.errors?.['invalidFormat']){
            <small class="p-error">Formato esperado: P999999/2024</small>
          }
        </div>

        <!-- Tipo de Credencial -->
        <div class="form-field">
          <label class="field-label">Tipo de Credencial <span class="required">*</span></label>
          <p-dropdown
            formControlName="credentialType"
            [options]="credentialTypes"
            optionLabel="label"
            optionValue="value"
            placeholder="Selecione o tipo"
            styleClass="w-full"
            [style]="{'width': '100%'}">
          </p-dropdown>
        </div>

        <!-- Prazo (condicional para PCD Temporário) -->
        @if(this.form.value.credentialType === 'PCDTEMPORARY'){
          <div class="form-field">
            <label class="field-label">Prazo <span class="required">*</span></label>
            <p-dropdown
              formControlName="prompt"
              [options]="promptOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Selecione o prazo"
              styleClass="w-full"
              [style]="{'width': '100%'}">
            </p-dropdown>
          </div>
        }

        <!-- É TEA? (condicional para PCD Permanente) -->
        @if(this.form.value.credentialType === 'PCDPERMANENT'){
          <div class="form-field tea-field">
            <label class="field-label">É TEA?</label>
            <div class="tea-toggle">
              <span class="tea-label" [class.tea-active]="form.get('isTea')?.value">
                {{ form.get('isTea')?.value ? 'Sim, é TEA' : 'Não é TEA' }}
              </span>
              <p-inputSwitch formControlName="isTea"></p-inputSwitch>
            </div>
          </div>
        }

        <!-- Observação -->
        <div class="form-field">
          <label class="field-label">Observação</label>
          <textarea
            pInputTextarea
            formControlName="observation"
            placeholder="Digite observações adicionais..."
            rows="4"
            class="text-capitalize"
            [style]="{'width': '100%', 'resize': 'none'}">
          </textarea>
        </div>

        <!-- Botões de Ação -->
        <div class="dialog-actions">
          <p-button
            label="Cancelar"
            severity="secondary"
            (onClick)="dialogRef.close()"
            styleClass="action-button">
          </p-button>
          
          <p-button
            [label]="isSubmitting ? 'Processando...' : 'Emitir'"
            [icon]="isSubmitting ? 'pi pi-spin pi-spinner' : 'pi pi-check'"
            (onClick)="emitirCredential()"
            [disabled]="isSubmitting || !form.valid"
            styleClass="action-button">
          </p-button>
        </div>
      </form>
    </div>
  `,
  styles: [
    `
      /* Container Principal */
      .credentials-dialog {
        padding: 1.5rem;
        max-width: 600px;
        width: 100%;
      }

      .dialog-title {
        color: #325565;
        font-size: 1.5rem;
        font-weight: 600;
        margin: 0 0 1.5rem 0;
        padding-bottom: 0.75rem;
        border-bottom: 2px solid #FFE6CB;
      }

      /* Form */
      .credential-form {
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }

      .form-field {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }

      .field-label {
        font-weight: 600;
        color: #325565;
        font-size: 0.875rem;
      }

      .required {
        color: #F15A22;
        margin-left: 2px;
      }

      /* Input Styles */
      :host ::ng-deep .p-inputtext {
        width: 100%;
        font-size: 0.875rem;
        border: 1px solid #d1d5db;
        border-radius: 4px;
      }

      :host ::ng-deep .p-inputtext:enabled:focus {
        outline: none;
        border-color: rgb(16, 148, 158);
        box-shadow: 0 0 0 0.2rem rgba(16, 148, 158, 0.25);
      }

      :host ::ng-deep .p-inputtext:disabled {
        background-color: #f3f4f6;
        color: #6b7280;
      }

      :host ::ng-deep .p-inputtext.p-invalid {
        border-color: #F15A22;
      }

      /* Dropdown Styles */
      :host ::ng-deep .p-dropdown {
        width: 100%;
        font-size: 0.875rem;
        border: 1px solid #d1d5db;
      }

      :host ::ng-deep .p-dropdown:not(.p-disabled):hover {
        border-color: rgb(16, 148, 158);
      }

      :host ::ng-deep .p-dropdown:not(.p-disabled).p-focus {
        outline: none;
        border-color: rgb(16, 148, 158);
        box-shadow: 0 0 0 0.2rem rgba(16, 148, 158, 0.25);
      }

      /* Textarea Styles */
      :host ::ng-deep .p-inputtextarea {
        font-size: 0.875rem;
        border: 1px solid #d1d5db;
        border-radius: 4px;
      }

      :host ::ng-deep .p-inputtextarea:enabled:focus {
        outline: none;
        border-color: rgb(16, 148, 158);
        box-shadow: 0 0 0 0.2rem rgba(16, 148, 158, 0.25);
      }

      /* Text Transform */
      .text-capitalize {
        text-transform: capitalize;
      }

      .text-uppercase {
        text-transform: uppercase;
        font-family: monospace;
      }

      /* Error Message */
      :host ::ng-deep .p-error {
        color: #F15A22;
        font-size: 0.75rem;
        margin-top: 0.25rem;
        font-weight: 500;
      }

      /* TEA Toggle Field */
      .tea-field {
        margin: 0.5rem 0;
      }

      .tea-toggle {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0.75rem 1rem;
        border: 1px solid #d1d5db;
        border-radius: 4px;
        background-color: #ffffff;
      }

      .tea-label {
        font-weight: 500;
        color: #6b7280;
        font-size: 0.875rem;
      }

      .tea-label.tea-active {
        color: rgb(16, 148, 158);
        font-weight: 600;
      }

      /* InputSwitch Customization */
      :host ::ng-deep .p-inputswitch.p-inputswitch-checked .p-inputswitch-slider {
        background: rgb(16, 148, 158);
      }

      /* Dialog Actions */
      .dialog-actions {
        display: flex;
        gap: 0.75rem;
        justify-content: flex-end;
        margin-top: 1.5rem;
        padding-top: 1rem;
        border-top: 1px solid #FFE6CB;
      }

      /* Button Customization */
      :host ::ng-deep .action-button .p-button {
        font-size: 0.875rem;
        padding: 0.5rem 1.5rem;
        font-weight: 600;
      }

      :host ::ng-deep .p-button.p-button-secondary {
        background: #6b7280;
        border-color: #6b7280;
      }

      :host ::ng-deep .p-button.p-button-secondary:hover {
        background: #4b5563;
        border-color: #4b5563;
      }

      :host ::ng-deep .p-button:not(.p-button-secondary) {
        background: rgb(16, 148, 158);
        border-color: rgb(16, 148, 158);
      }

      :host ::ng-deep .p-button:not(.p-button-secondary):hover:not(:disabled) {
        background: rgb(6, 182, 212);
        border-color: rgb(6, 182, 212);
      }

      :host ::ng-deep .p-button:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }

      /* Toast Customization */
      :host ::ng-deep .p-toast {
        z-index: 9999;
      }

      /* Responsive Design */
      @media (max-width: 768px) {
        .credentials-dialog {
          padding: 1rem;
        }

        .dialog-actions {
          flex-direction: column;
        }

        :host ::ng-deep .action-button {
          width: 100%;
        }
      }
    `,
  ],
})
export class CredentialsInformComponent implements OnInit {
  @ViewChild('processNumberInput')
  processNumberInput!: ElementRef<HTMLInputElement>;
  form: FormGroup;
  isSubmitting: boolean = false;

  credentialTypes = [
    { label: 'Idoso', value: 'IDOSO' },
    { label: 'PCD Permanente', value: 'PCDPERMANENT' },
    { label: 'PCD Temporário', value: 'PCDTEMPORARY' }
  ];

  promptOptions = [
    { label: 'Seis Meses', value: 'SEIS_MESES' },
    { label: 'Sete Meses', value: 'SETE_MESES' },
    { label: 'Oito Meses', value: 'OITO_MESES' },
    { label: 'Nove Meses', value: 'NOVE_MESES' },
    { label: 'Um Ano', value: 'UM_ANO' },
    { label: 'Dois Anos', value: 'DOIS_ANOS' },
    { label: 'Cinco Anos', value: 'CINCO_ANOS' }
  ];

  constructor(
    public credentialsService: CredentialsService,
    public peopleService: PeopleService,
    public dialogRef: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private http: HttpClient,
    private fb: FormBuilder,
    private dialogService: DialogService,
    private router: Router,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef,
    private credentialsStateService: CredentialsStateService
  ) {
    const data = this.config.data as CredentialRequest;
    this.form = this.fb.group({
      recipientName: [data.recipientName, Validators.required],
      applicantName: [
        data.applicantName || 'Solicitante é o Beneficiário',
      ],
      processNumber: [
        data.processNumber,
        [Validators.required, this.processNumberValidator],
      ],
      credentialType: ['', Validators.required],
      prompt: [data.prompt || 'UM_ANO'],
      observation: [data.observation || ''],
      isTea: [data.isTea || false],
    });
  }

  teste: boolean = false;

  ngOnInit(): void {
    this.cdr.detectChanges();
    const data = this.config.data as CredentialRequest;

    // Formata o número do processo inicial se existir
    if (data.processNumber) {
      setTimeout(() => {
        this.formatInitialProcessNumber();
      });
    }
    // Foca no campo após um pequeno delay - abordagem mais agressiva
    setTimeout(() => this.focusProcessNumberField(), 300);
  }

  // Método para focar no campo processNumber
  private focusProcessNumberField(): void {
    if (this.processNumberInput?.nativeElement) {
      this.processNumberInput.nativeElement.focus();

      // Força o foco múltiplas vezes se necessário
      const focusAttempts = setInterval(() => {
        if (document.activeElement !== this.processNumberInput.nativeElement) {
          this.processNumberInput.nativeElement.focus();
        } else {
          clearInterval(focusAttempts);
        }
      }, 50);

      // Para após 2 segundos
      setTimeout(() => clearInterval(focusAttempts), 2000);
    }
  }

  // Formata o valor inicial do processo
  private formatInitialProcessNumber(): void {
    const control = this.form.get('processNumber');
    if (control?.value) {
      let value = control.value.toString().toUpperCase();

      // Se já começa com P e tem formato aproximado, formata
      if (value.startsWith('P')) {
        this.validateProcessNumber();
      } else {
        // Se não começa com P, adiciona e formata
        const numbersOnly = value.replace(/\D/g, '');
        if (numbersOnly.length > 0) {
          control.setValue('P' + numbersOnly);
          this.validateProcessNumber();
        }
      }
    }
  }

  // Validador personalizado para o número do processo
  private processNumberValidator(control: any) {
    if (!control.value) {
      return null;
    }

    const value = control.value.toString();
    const pattern = /^P\d{6}\/\d{4}$/;

    if (!pattern.test(value)) {
      return { invalidFormat: true };
    }

    return null;
  }

  // Permite apenas números, P/p e backspace
  onKeyPress(event: KeyboardEvent): void {
    const allowedChars = '0123456789Pp/';
    const key = event.key;

    if (
      !allowedChars.includes(key) &&
      key !== 'Backspace' &&
      key !== 'Delete' &&
      key !== 'Tab' &&
      key !== 'ArrowLeft' &&
      key !== 'ArrowRight'
    ) {
      event.preventDefault();
    }
  }

  // Formatação durante a digitação
  formatProcessNumber(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.toUpperCase();

    // Remove caracteres inválidos (mantém apenas números, P e /)
    value = value.replace(/[^0-9P\/]/g, '');

    // Garante que só tem uma barra
    const slashCount = (value.match(/\//g) || []).length;
    if (slashCount > 1) {
      value = value.replace(/\//g, '');
      const parts = value.split('');
      parts.splice(7, 0, '/');
      value =
        parts.join('').replace(/\//g, '').substring(0, 7) +
        '/' +
        value.substring(7);
      value = value.replace(/\/+/g, '/');
    }

    // Limita o tamanho máximo
    if (value.length > 12) {
      value = value.substring(0, 12);
    }

    // Atualiza o valor no input
    input.value = value;

    // Atualiza o form control
    this.form.get('processNumber')?.setValue(value, { emitEvent: false });
  }

  // Formatação final quando perde o foco
  validateProcessNumber(): void {
    const control = this.form.get('processNumber');
    if (!control?.value) {
      control?.setErrors({ invalidFormat: true });
      return;
    }

    let value = control.value
      .toString()
      .toUpperCase()
      .replace(/[^0-9\/]/g, '');

    // Garante que começa com P
   /* if (!value.startsWith('P')) {
      value = 'P' + value;
    }*/

    // Insere uma barra depois do sexto dígito
    if (value.length > 6 && !value.includes('/')) {
      value = value.substring(0, 6) + '/' + value.substring(6);
    }

    // Se está vazio ou só tem 'P', marca como inválido
   /* if (!value || value === 'P') {
      control.setErrors({ invalidFormat: true });
      return;
    }*/

    // Remove o P se existir no início para processar os números
    const hasP = value.startsWith('P');
    const numbersPart = hasP ? value.substring(1) : value;
    const numbersOnly = numbersPart.replace(/\D/g, '');

    // Se não tem números suficientes, marca como inválido
    if (numbersOnly.length === 0) {
      control.setErrors({ invalidFormat: true });
      return;
    }

    // Separa em parte antes e depois da barra
    let beforeSlash = numbersOnly.substring(0, 6);
    let afterSlash = numbersOnly.substring(6, 10);

    // Completa com zeros à esquerda para ter 6 dígitos antes da barra
    //beforeSlash = beforeSlash.padStart(6, '0');

    //Completa com '20' à esquerda para ter 4 dígitos depois da barra e garantir ano válido com quatro caracteres
      if (afterSlash.length === 4) {
      afterSlash = afterSlash;
    } else {
      afterSlash = new Date().getFullYear().toString();
    }

    // Monta o valor final
    const formattedValue = `P${beforeSlash}/${afterSlash}`;

    // Atualiza o valor
    control.setValue(formattedValue);

    // Remove erros se estiver válido
    if (formattedValue.length === 12) {
      control.setErrors(null);
    } else {
      control.setErrors({ invalidFormat: true });
    }

    control.updateValueAndValidity();
  }

  emitirCredential(): void {
    // Valida e formata o número do processo antes de enviar
    this.validateProcessNumber();

    if (this.form.valid && !this.isSubmitting) {
      this.isSubmitting = true;
      const data = this.config.data as CredentialRequest;

      // Garante que o número está no formato correto
      const processNumber = this.form.get('processNumber')?.value || '';
      data.processNumber = processNumber.toUpperCase();

      data.observation = this.form.get('observation')?.value || '';
      data.prompt = this.form.get('prompt')?.value || '';
      data.isTea = this.form.get('isTea')?.value || false;

      if (!data.recipientId) {
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Beneficiário não fornecido. Verifique e tente novamente.',
          life: 5000
        });
        this.isSubmitting = false;
        return;
      }

      this.credentialsService
        .setCredentialsData(data, this.form.get('credentialType')?.value)
        .subscribe({
          next: (response: { blob: Blob; credentialNumber: string }) => {
            const { blob, credentialNumber } = response;
            const data = this.config.data as CredentialRequest;

            this.credentialsStateService.notifyCredentialCreated();
            this.dialogRef.close();

            const dialogRef = this.dialogService.open(CredentialsViewComponent, {
              data: {
                blob,
                fileName: `Credencial_${
                  credentialNumber +
                  ' ' +
                  data.recipientName +
                  ' ' +
                  this.form.get('credentialType')?.value
                }`,
              },
              width: '90vw',
              height: '87vh',
              styleClass: 'full-screen-modal',
            });

            if (dialogRef) {
              dialogRef.onClose.subscribe(() => {
                this.router.navigate(['/home/emission']);
                this.isSubmitting = false;
              });
            }
          },
          error: (err: HttpErrorResponse) => {
            this.isSubmitting = false;

            let message: string;

            switch (err.status) {
              case 409:
                message =
                  'Este Beneficiário já possui uma Credencial Ativa para o Tipo Solicitado.';
                break;
              case 422:
                message = 'Este Beneficiário ainda não tem 60 anos de idade.';
                break;
              case 500:
                message =
                  'Verifique a documentação obrigatória do beneficiário.';
                break;
              case 406:
                message =
                  'Verifique a documentação obrigatória do beneficiário.';
                break;
              default:
               // message = `Ocorreu um erro inesperado. Tente novamente mais tarde. (Status: ${err.status})`;
                message = err.error?.message || 'Verifique a documentação exigida.';
                break;
            }

            this.messageService.add({
              severity: 'error',
              summary: 'Erro',
              detail: message,
              life: 5000
            });
          },
        });
    } else {
      // Mostra erro específico para o processo se for o caso
      if (this.form.get('processNumber')?.errors?.['invalidFormat']) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Atenção',
          detail: 'Número do processo inválido. Formato esperado: P999999/2024',
          life: 5000
        });
      } else {
        this.messageService.add({
          severity: 'warn',
          summary: 'Atenção',
          detail: 'O formulário está inválido. Preencha todos os campos obrigatórios.',
          life: 5000
        });
      }
    }
  }

  public isUnderThanSixty(birthDate: Date): boolean {
    if (!birthDate) {
      return false; // Retorna false caso birthDate seja nulo ou inválido
    }

    const currentDate = new Date(); // Data atual
    const age = currentDate.getFullYear() - birthDate.getFullYear(); // Diferença em anos
    const month = currentDate.getMonth() - birthDate.getMonth(); // Diferença no mês

    // Ajusta se a data de aniversário ainda não passou neste ano
    if (
      month < 0 ||
      (month === 0 && currentDate.getDate() < birthDate.getDate())
    ) {
      return age < 60;
    }

    return age < 60; // Se a idade for menor que 60, retorna true
  }
}
